import React, { useMemo } from 'react';
import { RightSectionContainer, GridContainer, MemberRow, OuterWrapper } from './styles';
import WorkloadDayCell from './workloadCell';
import {
  WorkloadReportMemberInterface,
  WorkloadWeeklyScheduleInterface,
  SelectRangeInterface
} from '../../../interfaces/ReportInterface';
import AppLoading from '../../loading/appLoading';
import EmptyStateV2 from '../../emptyState';
import { EMPTY_STATE_SIZE } from '../../../global/constants';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';

// Initialize dayjs plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

interface WorkspaceUserInterface {
  id: string;
  name: string;
  profile_photo?: string;
  avatar?: { avatar?: string };
}

interface WorkloadRightSectionProps {
  workloadData?: WorkloadReportMemberInterface[] | null;
  loading?: boolean;
  workspaceUsers?: WorkspaceUserInterface[];
  selectedUserIds?: string[]; // selected users from filter
  selectionRange?: SelectRangeInterface;
}

const WorkloadRightSection: React.FC<WorkloadRightSectionProps> = ({
  workloadData,
  loading,
  workspaceUsers = [],
  selectedUserIds,
  selectionRange
}) => {
  // build list of days from selectionRange
  const allDates = useMemo(() => {
    const start = selectionRange ? dayjs(selectionRange.startDate) : dayjs().startOf('isoWeek');
    const end = selectionRange ? dayjs(selectionRange.endDate) : dayjs(start).add(6, 'day');
    const dates: string[] = [];
    let current = start;
    while (current.isSameOrBefore(end, 'day')) {
      dates.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'day');
    }
    return dates;
  }, [selectionRange]);

  // prepare users to render: filter by selectedUserIds if present otherwise all non-agent users
  const usersToRender = useMemo(() => {
    const baseUsers =
      selectedUserIds && selectedUserIds.length > 0
        ? workspaceUsers.filter((u) => selectedUserIds.includes(u.id))
        : workspaceUsers;
    return baseUsers.filter((u) => !!u?.id && !u.id.toLowerCase().startsWith('agent_'));
  }, [workspaceUsers, selectedUserIds]);

  // Helper to create empty weeklySchedule for a user (planned/empty cells & weekend)
  const buildEmptySchedule = (dates: string[]): WorkloadWeeklyScheduleInterface[] => {
    return dates.map((dateStr) => {
      const dateDayjs = dayjs(dateStr);
      const dayOfWeek = dateDayjs.day();
      const dayName = dateDayjs.format('ddd');
      const dayNumber = dateDayjs.date();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return {
          date: dateStr,
          day: dayName,
          dayNumber,
          type: 'weekend',
          hours: '',
          color: 'grey',
          label: 'Weekend'
        };
      }
      return {
        date: dateStr,
        day: dayName,
        dayNumber,
        type: 'planned',
        hours: '',
        color: 'brown'
      };
    });
  };

  // For each user, merge API data if available; otherwise use empty schedule
  const rows = useMemo(() => {
    return usersToRender.map((user) => {
      const data = workloadData?.find((m) => m.id === user.id);
      if (data) {
        // already transformed in store/service: just use it
        return data;
      } else {
        // create empty member shaped object
        return {
          id: user.id,
          name: user.name,
          avatarUrl: user.profile_photo || user.avatar?.avatar || '',
          totalHours: '0h',
          overloadTime: '0h',
          tasksCount: 0,
          projectsCount: 0,
          weeklySchedule: buildEmptySchedule(allDates),
          dates: [],
          totalOvertimeHours: 0
        } as WorkloadReportMemberInterface;
      }
    });
  }, [usersToRender, workloadData, allDates]);

  return (
    <>
      {loading && (
        <OuterWrapper>
          <AppLoading />
        </OuterWrapper>
      )}
      {!loading && (!selectedUserIds || selectedUserIds?.length === 0) && (
        <OuterWrapper>
          <EmptyStateV2
            size={EMPTY_STATE_SIZE.MEDIUM}
            title='No members are selected'
            imageUrl={'customer_empty'}
            isAlignedCenter={true}
          />
        </OuterWrapper>
      )}
      {!loading && selectedUserIds && selectedUserIds?.length > 0 && (
        <RightSectionContainer>
          <GridContainer>
            {/* Member Rows */}
            {rows.map((member: WorkloadReportMemberInterface) => (
              <MemberRow key={member.id}>
                {member.weeklySchedule?.map((daySchedule, dayIndex) => (
                  <WorkloadDayCell key={`${member.id}-${dayIndex}`} daySchedule={daySchedule} />
                ))}
              </MemberRow>
            ))}
          </GridContainer>
        </RightSectionContainer>
      )}
    </>
  );
};

export default WorkloadRightSection;
